// How a nominee ("nominate a loved one", PPL-3600) is related to the
// policyholder.
//
// A DELIBERATE COPY of EMERGENCY_CONTACT_RELATIONSHIPS in the patient platform
// (perci-platform-backend, bff-clinical/members/emergencyContactRelationships.ts),
// which stays the source of truth: it carries the FHIR codings — v3-RoleCode
// and SNOMED — that this list has no use for and does not repeat. Keep the
// displays below identical to the `display` values there; the test beside this
// file pins them.
//
// The wire contract is unchanged and stays free text (ReferralNomineeSchema).
// The platform normalises whatever string it receives — lowercased, with
// spaces, underscores and hyphens removed — and matches it against both its
// own `code` and its `display`, so submitting a display below resolves to a
// coded RelatedPerson.relationship rather than falling through to "other".
//
// Drift between the two lists is therefore soft: a value the platform does not
// know is kept verbatim as the free text of its `other` entry, so it degrades
// to what this form produced before the dropdown existed rather than losing
// the answer.
export enum ReferralNomineeRelationship {
  spouse = 'spouse',
  civilPartner = 'civil-partner',
  partner = 'partner',
  mother = 'mother',
  father = 'father',
  parent = 'parent',
  son = 'son',
  daughter = 'daughter',
  child = 'child',
  brother = 'brother',
  sister = 'sister',
  sibling = 'sibling',
  grandparent = 'grandparent',
  grandchild = 'grandchild',
  aunt = 'aunt',
  uncle = 'uncle',
  niece = 'niece',
  nephew = 'nephew',
  cousin = 'cousin',
  carer = 'carer',
  legalGuardian = 'legal-guardian',
  powerOfAttorney = 'power-of-attorney',
  friend = 'friend',
  // The only one the platform marks `allowsFreeText`. A form offering this
  // option submits what the referrer typed, NOT the word "Other" — the
  // platform recognises no such relationship, files it under `other` and keeps
  // the typed answer as the text.
  other = 'other',
}

// The order the options are offered in: partners, then parents, children and
// siblings, then the wider family, then the non-family roles, with "Other"
// last. Matches the platform's own ordering.
export const referralNomineeRelationships: ReferralNomineeRelationship[] = [
  ReferralNomineeRelationship.spouse,
  ReferralNomineeRelationship.civilPartner,
  ReferralNomineeRelationship.partner,
  ReferralNomineeRelationship.mother,
  ReferralNomineeRelationship.father,
  ReferralNomineeRelationship.parent,
  ReferralNomineeRelationship.son,
  ReferralNomineeRelationship.daughter,
  ReferralNomineeRelationship.child,
  ReferralNomineeRelationship.brother,
  ReferralNomineeRelationship.sister,
  ReferralNomineeRelationship.sibling,
  ReferralNomineeRelationship.grandparent,
  ReferralNomineeRelationship.grandchild,
  ReferralNomineeRelationship.aunt,
  ReferralNomineeRelationship.uncle,
  ReferralNomineeRelationship.niece,
  ReferralNomineeRelationship.nephew,
  ReferralNomineeRelationship.cousin,
  ReferralNomineeRelationship.carer,
  ReferralNomineeRelationship.legalGuardian,
  ReferralNomineeRelationship.powerOfAttorney,
  ReferralNomineeRelationship.friend,
  ReferralNomineeRelationship.other,
];

// What the referrer reads in the dropdown, and — for everything but `other` —
// the exact string submitted as `nominee.relationship`.
export function displayReferralNomineeRelationship(
  value: ReferralNomineeRelationship,
) {
  switch (value) {
    case ReferralNomineeRelationship.spouse:
      return 'Spouse';
    case ReferralNomineeRelationship.civilPartner:
      return 'Civil Partner';
    case ReferralNomineeRelationship.partner:
      return 'Partner';
    case ReferralNomineeRelationship.mother:
      return 'Mother';
    case ReferralNomineeRelationship.father:
      return 'Father';
    case ReferralNomineeRelationship.parent:
      return 'Parent';
    case ReferralNomineeRelationship.son:
      return 'Son';
    case ReferralNomineeRelationship.daughter:
      return 'Daughter';
    case ReferralNomineeRelationship.child:
      return 'Child';
    case ReferralNomineeRelationship.brother:
      return 'Brother';
    case ReferralNomineeRelationship.sister:
      return 'Sister';
    case ReferralNomineeRelationship.sibling:
      return 'Sibling';
    case ReferralNomineeRelationship.grandparent:
      return 'Grandparent';
    case ReferralNomineeRelationship.grandchild:
      return 'Grandchild';
    case ReferralNomineeRelationship.aunt:
      return 'Aunt';
    case ReferralNomineeRelationship.uncle:
      return 'Uncle';
    case ReferralNomineeRelationship.niece:
      return 'Niece';
    case ReferralNomineeRelationship.nephew:
      return 'Nephew';
    case ReferralNomineeRelationship.cousin:
      return 'Cousin';
    case ReferralNomineeRelationship.carer:
      return 'Carer';
    case ReferralNomineeRelationship.legalGuardian:
      return 'Legal Guardian';
    case ReferralNomineeRelationship.powerOfAttorney:
      return 'Power of Attorney';
    case ReferralNomineeRelationship.friend:
      return 'Friend';
    case ReferralNomineeRelationship.other:
      return 'Other';
    default:
      throw new Error(`Unknown referral nominee relationship: ${value}`);
  }
}

export function isReferralNomineeRelationship(
  value: unknown,
): value is ReferralNomineeRelationship {
  return Object.values<unknown>(ReferralNomineeRelationship).includes(value);
}
